import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useLocation from 'hooks/useLocation';
import { useMemo } from 'react';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import {
  useGet15DayForecastQuery,
  useGet7DayHourlyForecastQuery,
  useGetDetailedForecastQuery,
} from 'services/weather/weather.api';
import ForecastChartContent from './components/ForecastChartContent/ForecastChartContent';
import { buildForecastChart, getForecastChartConfig } from './utils/forecastChart.utils';

/**
 * Loads the forecast needed by the selected chart and prepares it for display.
 * The page supplies the chart ID; this feature owns the forecast queries,
 * chart construction, loading indicator, and retry after a failed request.
 */
const ForecastCharts = ({ chartId, showTimeUpdate = false }) => {
  const { locations } = useLocation();
  const weatherLocation = locations.weatherLocation;
  const chartConfig = getForecastChartConfig(chartId);

  // Retain the page's three forecast subscriptions and reuse their RTK Query cache.
  const detailedForecastQuery = useGetDetailedForecastQuery(weatherLocation);
  const hourlyForecastQuery = useGet7DayHourlyForecastQuery(weatherLocation);
  const day15ForecastQuery = useGet15DayForecastQuery(weatherLocation);

  // Several charts share the 15-day response; only the selected source controls this view.
  const queriesBySource = {
    detailedForecast: detailedForecastQuery,
    hourlyForecast: hourlyForecastQuery,
    day15Forecast: day15ForecastQuery,
  };
  const selectedQuery = queriesBySource[chartConfig.sourceKey];
  const updateTime = showTimeUpdate ? (
    <WidgetTimeUpdate
      route="Charts"
      addMargin
      updatedAt={selectedQuery.fulfilledTimeStamp ?? null}
    />
  ) : null;

  // currentData belongs to this location, so a location change cannot show the previous city's
  // data; a refresh of the same location keeps it visible until the new response arrives.
  const chartData = useMemo(
    () => buildForecastChart(selectedQuery.currentData, chartConfig),
    [selectedQuery.currentData, chartConfig],
  );

  if (selectedQuery.isError) {
    return (
      <>
        {updateTime}
        <ErrorMessage
          message={getApiErrorMessage(selectedQuery.error)}
          onRetry={selectedQuery.refetch}
          retryLabel="Retry Forecast"
        />
      </>
    );
  }

  // Keep an existing chart visible during refresh; finished empty results use BaseChart's message.
  if (selectedQuery.isFetching && !chartData) {
    return (
      <>
        {updateTime}
        <WidgetLoadingAnimation />
      </>
    );
  }

  return (
    <>
      {updateTime}
      <ForecastChartContent chartData={chartData} chartType={chartConfig.chartType} />
    </>
  );
};

export default ForecastCharts;
